<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { useDisplay } from 'vuetify'
import { useRouter } from 'vue-router'
import PermissionGate from './PermissionGate.vue'
import UnathorizedState from './UnauthorizedState.vue'

const props = withDefaults(
  defineProps<{
    /* Título */
    title?: string
    /* Mostrar título? */
    showTitle?: boolean
    /* Header “grudado” no topo ao rolar */
    sticky?: boolean
    /* Largura máxima */
    maxWidth?: number
    /* Alinhamento do container*/
    align?: 'left' | 'center'
    /* Elevação do card */
    elevation?: number
    /* Exibe FAB no mobile */
    mobileFab?: boolean
    /* Mostra skeleton apenas na 1ª carga */
    skeletonOnce?: boolean
    progressAfterFirst?: boolean
    /* Estado de loading (skeleton da view) */
    loading?: boolean
    /* Tipo do skeleton do Vuetify */
    skeletonType?: string

    /* ===== Permissões (mesma ideia do BaseListLayout) ===== */
    required?: string | string[]
    mode?: 'any' | 'all'
    needsAdmin?: boolean
    autoLoad?: boolean
    mobileFabDisabled?: boolean
  }>(),
  {
    title: '',
    showTitle: true,
    sticky: true,
    maxWidth: 1260,
    align: 'center',
    elevation: 1,
    mobileFab: false,
    skeletonOnce: true,
    progressAfterFirst: true,
    loading: false,
    skeletonType: 'table',
    mobileFabDisabled: false,
    required: undefined,
    mode: 'any',
    needsAdmin: false,
    autoLoad: true,
  }
)

const emit = defineEmits<{
  (e: 'fab-click'): void
  (e: 'fab-disabled-click'): void
}>()

const router = useRouter()
const { mobile } = useDisplay()
const isMobile = computed(() => mobile.value)

const sheetVariant = computed(() =>
  props.elevation === 0 ? 'flat' : 'elevated'
)

/* Alinhamento do container */
const containerAlignClass = computed(() =>
  props.align === 'left' ? 'ml-0 mr-auto' : 'mx-auto'
)

/* Classes utilitárias no wrapper central */
const containerClasses = computed(() => [containerAlignClass.value])

const containerStyle = computed(() => {
  if (isMobile.value) {
    return {
      maxWidth: '100vw',
      width: '100vw',
    }
  }
  return {
    maxWidth: `${props.maxWidth}px`,
    width: '100%',
  }
})

/* Estilos do sheet do conteúdo */
const sheetProps = computed(() => {
  if (isMobile.value) {
    return {
      elevation: 0,
      variant: 'flat' as const,
      rounded: 0 as const,
    }
  }
  return {
    elevation: props.elevation,
    variant: sheetVariant.value as 'flat' | 'elevated',
  }
})

/* ------ CONTROLE DO SKELETON (view) ------ */
const firstPaintDone = ref(false)

watch(
  () => props.loading,
  async (isLoading) => {
    if (!isLoading && !firstPaintDone.value) {
      await nextTick()
      firstPaintDone.value = true
    }
  },
  { immediate: true }
)

const showSkeleton = computed(() => {
  if (!props.loading) return false
  return props.skeletonOnce ? !firstPaintDone.value : true
})

const showTopProgress = computed(() => {
  return !!(props.progressAfterFirst && props.loading && firstPaintDone.value)
})

/* Permissão que vamos exibir no UnauthorizedState:
   se for array, usamos a primeira; se não tiver, fica undefined
   e o componente cai na mensagem genérica */
const unauthorizedPermission = computed(() => {
  if (!props.required) return undefined
  return Array.isArray(props.required) ? props.required[0] : props.required
})
</script>

<template>
  <!-- IMPORTANTE: não usar v-app/v-main aqui -->
  <div class="bg-grey-lighten-5 w-100 top_nav">
    <PermissionGate
      :required="required"
      :mode="mode"
      :needs-admin="needsAdmin"
      :auto-load="autoLoad"
    >
      <!-- fallback não autorizado -->
      <template #fallback>
        <UnathorizedState
          :permission="unauthorizedPermission"
          cta-label="Pedir acesso"
          @cta="() => router.push({ name: 'profile' })"
        />
      </template>

      <!-- HEADER (tabs + ações) -->
      <v-sheet
        :class="sticky ? 'position-sticky' : ''"
        class="border-b-thin"
        style="height: 48px"
      >
        <div v-if="showTitle">
          <slot name="header-actions" />
        </div>

        <slot name="tabs" />

        <v-progress-linear
          v-if="showTopProgress"
          indeterminate
          height="2"
          color="primary"
          class="w-100"
        />
      </v-sheet>

      <!-- CONTEÚDO -->
      <div
        class="content-wrap"
        :class="[isMobile ? 'pa-0 mt-n6' : 'pa-6', ...containerClasses]"
        :style="containerStyle"
      >
        <v-sheet
          class="w-100 d-flex flex-column border-thin content-sheet"
          :class="isMobile ? 'pa-2' : 'pa-6'"
          :variant="sheetProps.variant"
          :style="{
            borderRadius: isMobile ? '0' : '16px',
            borderColor: '#E0E0E0',
          }"
        >
          <v-fade-transition>
            <div v-if="showSkeleton" class="pa-4">
              <slot name="skeleton">
                <v-skeleton-loader
                  :type="skeletonType"
                  boilerplate
                  style="border-radius: 16px"
                />
              </slot>
            </div>

            <div v-else>
              <slot />
            </div>
          </v-fade-transition>
        </v-sheet>
      </div>

      <slot name="footer" />

      <!-- FAB mobile só para quem está autorizado -->
      <v-btn
        v-if="mobileFab && isMobile"
        icon="mdi-plus"
        color="primary"
        variant="flat"
        class="position-fixed z-2 elevation-2 bottom-14 right-10 text-white"
        :class="{ 'fab-disabled': mobileFabDisabled }"
        @click="
          mobileFabDisabled ? emit('fab-disabled-click') : emit('fab-click')
        "
      />
    </PermissionGate>
  </div>
</template>

<style scoped>
:deep(.tabs-underline .v-tab) {
  font-weight: 550 !important;
  letter-spacing: 0 !important;
  text-transform: uppercase;
  opacity: 0.65;
}
:deep(.tabs-underline .v-tab.v-tab--selected) {
  border-bottom-color: var(--v-theme-green);
  opacity: 1;
}
:deep(.tabs-underline .v-tabs-slider) {
  display: none;
}

.fab-disabled {
  pointer-events: auto !important; /* mantém clicável */
  background-color: #e0e0e0 !important;
  color: #a6a6a6 !important;
  border-color: #e0e0e0 !important;
}

.top_nav {
  /* em vez de margin-top */
  padding-top: 64px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Header (tabs) ocupa o topo */
.top_nav > .position-sticky,
.top_nav > .border-b-thin {
  flex: 0 0 auto;
}

/* Conteúdo estica */
.content-wrap {
  flex: 1 1 auto;
  display: flex;
  min-height: 0; /* importante em layouts flex */
}

/* Sheet precisa “crescer” também */
.content-sheet {
  flex: 1 1 auto;
  min-height: 100%;
}
</style>
